/**
 * Área FICHA · MENSAGENS: a conversa da consultora com a cliente, por lead.
 *
 * Cobre as três chamadas de banco da aba no legado: a lista (`admin.html:5755`), a marca de lidas
 * (`5810`) e o envio (`5854`). O anexo sobe pelo Storage (`ficha-arquivos.ts`, `enviarArquivo`) e
 * abre por link assinado (`gerarLinkAssinado`), como no legado.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  comEscopo,
  executar,
  registroComLead,
  type Escopo,
  type FonteDeDados,
  type LinhaMensagens,
  type Resultado,
} from '../nucleo.js';

/**
 * Lista as mensagens da ficha aberta, da mais antiga para a mais nova.
 * Legado: `admin.html:5755` a `5758`, `comEscopo(sb.from('mensagens').select('*'))
 * .order('criado_em', { ascending: true })`.
 */
export function listarMensagens(fonte: FonteDeDados, escopo: Escopo): Promise<Resultado<readonly LinhaMensagens[] | null>> {
  // A ordem crescente é a da conversa: a última mensagem fica no fim, como num chat.
  return executar<readonly LinhaMensagens[] | null>(
    comEscopo(clienteDe(fonte).from('mensagens').select('*'), escopo).order('criado_em', { ascending: true }),
  );
}

/**
 * Marca como lidas as mensagens da cliente que a consultora acabou de ver.
 * Legado: `admin.html:5810`, `sb.from('mensagens').update({ lida_em: agora }).in('id', ids)`.
 *
 * O legado não confere o resultado desta chamada; a tela nova também não mostra nada se ela
 * falhar, e a próxima carga tenta de novo, porque a mensagem continua sem `lida_em`.
 */
export function marcarMensagensComoLidas(
  fonte: FonteDeDados,
  ids: readonly number[],
  lidaEm: string,
): Promise<Resultado<null>> {
  // Só os ids que a tela mostrou, e não um filtro por remetente, para não marcar o que chegou depois.
  return executar<null>(clienteDe(fonte).from('mensagens').update({ lida_em: lidaEm }).in('id', [...ids]));
}

/** O que a consultora envia: o texto (pode ser vazio com anexo) e o anexo, quando houver. */
export interface NovaMensagem {
  readonly conteudo: string;
  readonly anexoCaminho: string | null;
  readonly anexoNome: string | null;
}

/**
 * Grava a mensagem da consultora.
 * Legado: `admin.html:5854` a `5859`, `insert(registroComLead({ remetente: 'consultora', conteudo,
 * anexo_caminho, anexo_nome }))`.
 */
export function enviarMensagem(fonte: FonteDeDados, escopo: Escopo, nova: NovaMensagem): Promise<Resultado<null>> {
  // O remetente é fixo: esta tela é da consultora, e a cliente escreve pelo portal dela.
  const registro = registroComLead(escopo, {
    remetente: 'consultora',
    conteudo: nova.conteudo,
    anexo_caminho: nova.anexoCaminho,
    anexo_nome: nova.anexoNome,
  });
  // Insert simples, sem leitura encadeada: o legado recarrega a lista inteira depois.
  return executar<null>(clienteDe(fonte).from('mensagens').insert(registro));
}
